import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { useDownloadFiles } from "../../hooks/useDownloadFiles";
import { useScreenSize } from "../../hooks/useScreenSize";
import { DownloadIcon2 } from "../../icons/DownloadIcon2/DownloadIcon2";
import { toProductId } from "../../services/UUIDGeneratorService";
import { productSliceActions } from "../../store/products-slice";
import { MinimalProduct } from "../../types/product";
import { Popover } from "../Popover/Popover";
import { QuantityButton } from "../QuantityButton/QuantityButton";

import styles from "./material-list-item.module.css";

type MaterialListItemProps = {
  product: MinimalProduct;
  readOnly?: boolean;
  editMode?: boolean;
  setRelatedProducts?: any;
  openModal?: Dispatch<SetStateAction<boolean>>;
};

export const MaterialListItem = ({
  product,
  readOnly,
  editMode = false,
  setRelatedProducts,
  openModal
}: MaterialListItemProps) => {
  const { id, name, quantity, payExtraQuantity, relatedProducts } = product;
  const dispatch = useDispatch();
  const { isDesktop } = useScreenSize();
  const { fetchProductDocuments } = useDownloadFiles();
  const productId = toProductId(id.toString());
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(quantity > 0 ? quantity : "");
  const [editingPayExtra, setEditingPayExtra] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [payExtraInput, setPayExtraInput] = useState(
    product.payExtraQuantity?.toString() ?? ""
  );

  const handleQuantity = (id: string | number, increment: boolean) => {
    if (editMode) {
      if (increment) {
        dispatch(productSliceActions.incrementPayExtra(id));
      } else {
        dispatch(productSliceActions.decrementPayExtra(id));
      }
    } else {
      if (increment) {
        handleModalOpen();
        dispatch(productSliceActions.increment(id));
      } else {
        dispatch(productSliceActions.decrement(id));
      }
    }
  };

  const handleSpanClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();

    setEditingPayExtra(false);
    setShowInput(true);

    setInputValue(product.quantity.toString());
  };

  const handlePayExtraClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();

    if (!editMode) return;

    setShowInput(false);
    setEditingPayExtra(true);

    setPayExtraInput(product.payExtraQuantity?.toString() ?? "");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validValue = e.target.value.replace(/[^0-9.]/g, "");

    setInputValue(validValue);
    handleModalOpen();

    dispatch(
      productSliceActions.showInput({
        id,
        showInput: true,
      })
    );

    if (validValue === "") {
      return;
    }

    dispatch(
      productSliceActions.incrementFor({
        id,
        amount: Number(validValue),
      })
    );
  };

  const handleModalOpen = () => {
    if (quantity === 0 && relatedProducts?.length) {
      openModal?.(true);
      setRelatedProducts?.(relatedProducts);
    }
  }

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  return (
    <li
      className={`${styles["materials-list__card"]} ${readOnly && styles["materials-list__card--readonly"]
        }`}
      onClick={() => {
        if (inputValue == "") {
          setShowInput(false);
        }
      }}
    >
      <div className={styles["materials-list__left"]}>
        {readOnly && (
          <div className={styles["materials-list__quantity-readOnly"]}>
            <span>{quantity}</span>
          </div>
        )}

        <div
          className={
            isDesktop
              ? styles["materials-list__item--desktop"]
              : styles["materials-list__item--popover"]
          }
        >
          <span className={styles["materials-list__item"]}>{name}</span>

          {!isDesktop && (
            <Popover
              isOpen={showInfo}
              onClose={() => setShowInfo(false)}
              anchor={
                (payExtraQuantity ?? 0) > 0 ? (
                  <img
                    src="/img/info-icon.png"
                    alt="info"
                    className={styles["info-icon"]}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInfo((prev) => !prev);
                    }}
                  />
                ) : null
              }
            >
              <div>
                <div className={styles["popover-header"]}>
                  <strong>{name}</strong>
                  <button
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#8493AB",
                      fontSize: "18px",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 1,
                      width: "16px",
                      height: "16px",
                      display: "inline-flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onClick={() => setShowInfo(false)}
                  >
                    ×
                  </button>
                </div>

                <div className={styles.divider} />

                <div className={styles["popover-text"]}>
                  {payExtraQuantity ?? 0} von {quantity} wurden als
                  kostenpflichtig markiert.
                </div>
              </div>
            </Popover>
          )}

          {isDesktop && (payExtraQuantity ?? 0) > 0 && (
            <div className={styles["materials-list__pay-extra-quantity"]}>
              <span>
                {payExtraQuantity ?? 0} von {quantity} wurden als
                kostenpflichtig markiert
              </span>
            </div>
          )}
        </div>
      </div>
      {readOnly && (
        <div className={styles["materials-list__quantity-readOnly"]}>
          <DownloadIcon2
            handleDownloadButtonClicked={() => {
              fetchProductDocuments(productId);
            }}
          />
        </div>
      )}

      {!readOnly && !showInput && (
        <div className={styles["materials-list__quantity"]}>
          <QuantityButton
            active={quantity > 0}
            icon="decrement"
            onClick={() => handleQuantity(id, false)}
          />
          {!editMode && (
            <span
              className={styles["span__quantity"]}
              onClick={(e) => {
                handleSpanClick(e);
              }}
            >
              {product.quantity}
            </span>
          )}
          {editMode && (
            <span
              className={styles["span__quantity"]}
              onClick={handlePayExtraClick}
            >
              {editingPayExtra ? (
                <span className={styles["pay-extra-wrapper"]}>
                  <input
                    className={styles["pay-extra-input__quantity"]}
                    type="number"
                    step="0.01"
                    value={payExtraInput}
                    autoFocus
                    max={product.quantity}
                    min={0}
                    onChange={(e) => {
                      const val = e.target.value;

                      setPayExtraInput(val);

                      const parsed = Number(val);

                      if (!isNaN(parsed)) {
                        dispatch(
                          productSliceActions.setPayExtraQuantity({
                            id,
                            amount: Math.min(parsed, product.quantity),
                          })
                        );
                      }
                    }}
                    onBlur={() => setEditingPayExtra(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setEditingPayExtra(false);
                      }
                    }}
                  />
                  <span>/{product.quantity}</span>
                </span>
              ) : (
                <>
                  {product.payExtraQuantity ?? 0}/{product.quantity}
                </>
              )}
            </span>
          )}
          <QuantityButton
            active={quantity > 0}
            icon="increment"
            onClick={() => handleQuantity(id, true)}
          />
        </div>
      )}

      {showInput && !readOnly && (
        <div className={styles["materials-list__quantity"]}>
          <input
            className={styles["input__quantity"]}
            value={inputValue}
            ref={inputRef}
            type="number"
            onChange={(e) => {
              handleInputChange(e);
            }}
            onBlur={() => {
              setShowInput(false);
              dispatch(productSliceActions.showInput({ id, showInput: false }));
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
      )}
    </li>
  );
};
